import { Component, computed, inject } from '@angular/core';
import { RestService } from '@cumlaude/shared-services';
import { CronWorkflow, Workflow, WorkflowTemplate } from '@cumlaude/service-contract';
import { BehaviorSubject, filter, switchMap, timer } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { EtlService } from '../../services/etl.service';
import { TabMenuComponent } from '@cumlaude/shared-components-menu';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { TooltipDirective, TooltipElement } from '@cumlaude/shared-components-overlays';
import cronstrue from 'cronstrue';
import 'cronstrue/locales/nl';
import { CronExpressionParser } from 'cron-parser';
import { AuthService } from '@cumlaude/shared-authentication';
import { Router } from '@angular/router';
import { ToastrService } from '@cumlaude/shared-components-toast';

const nameToDescription: Map<string, string> = new Map([
	['delete-instellingen-cron', 'Cron job voor het opruimen van instellingen die geen gebruik meer maken van CumLaude'],
	['magister-daily-cron', 'Cron job voor het verwerken van de Magister scholen'],
	['magister-bulk-daily-cron', 'Cron job voor het bulk verwerken van de Magister scholen'],
	['magister-delta-daily-cron', 'Cron job voor het delta verwerken van de Magister scholen'],
	['somtoday-daily-cron', 'Cron job voor het verwerken van de Somtoday scholen'],
	['somtoday-bulk-daily-cron', 'Cron job voor het bulk verwerken van de Somtoday scholen'],
	['somtoday-delta-daily-cron', 'Cron job voor het delta verwerken van de Somtoday scholen'],
	[
		'weekly-cron',
		'Cron job voor het verwerken van landelijke data, zoals: brin, landelijke vakken, onderwijsresultaten, examencijfers, prestatieanalyse percentielen, vertaaltabellen, etc.',
	],
	['magister-scramble-cron', 'Cron job voor het maken van developer dumps van databases van de Magister scholen'],
]);

@Component({
	selector: 'app-etl-overzicht',
	templateUrl: './etl-overzicht.component.html',
	styleUrls: ['./etl-overzicht.component.scss'],
	imports: [TabMenuComponent, ButtonComponent, TooltipDirective],
})
export class EtlOverzichtComponent {
	private readonly restService = inject(RestService);
	private readonly etlService = inject(EtlService);
	protected readonly authService = inject(AuthService);
	protected readonly toastr = inject(ToastrService);
	private readonly router = inject(Router);

	private readonly refreshCronWorkflows$ = new BehaviorSubject<void>(undefined);
	private readonly refreshWorkflows$ = new BehaviorSubject<void>(undefined);

	available$ = this.restService.isArgoWorkflowsAvailable();
	readonly available = toSignal(this.available$);

	readonly cronWorkflows = toSignal(
		this.available$.pipe(
			filter((beschikbaar) => beschikbaar),
			switchMap(() => this.refreshCronWorkflows$),
			switchMap(() => timer(0, 60 * 1000)),
			switchMap(() => this.restService.getCronWorkflows())
		),
		{
			initialValue: [],
		}
	);

	readonly workflows = toSignal(
		this.available$.pipe(
			filter((beschikbaar) => beschikbaar),
			switchMap(() => this.refreshWorkflows$),
			switchMap(() => timer(0, 60 * 1000)),
			switchMap(() => this.restService.getWorkflows())
		),
		{
			initialValue: [],
		}
	);

	readonly workflowTemplates = toSignal(
		this.available$.pipe(
			filter((beschikbaar) => beschikbaar),
			switchMap(() => this.restService.getWorkflowTemplates())
		),
		{
			initialValue: [],
		}
	);

	readonly runningWorkflows = computed(() => this.workflows().filter((workflow) => workflow.status.phase === 'Running'));

	readonly landelijkeTemplates = computed(() =>
		this.workflowTemplates()
			.map((workflowTemplate) => this.etlService.mapWorkflowTemplate(workflowTemplate, this.runningWorkflows()))
			.filter((template) => !template.bron)
	);

	readonly cronJobs = computed(() => {
		const workflows = this.workflows();
		return this.cronWorkflows().map((cronWorkflow) => this.mapCronWorkflow(cronWorkflow, workflows));
	});

	readonly jobs = computed(() => this.workflows().map((workflow) => this.mapWorkflow(workflow)));

	async startLandelijkeETLJob(name: string, landelijkeTemplate: WorkflowTemplate) {
		const workflowStarted = await this.etlService.startLandelijkeWorkflow(landelijkeTemplate.metadata.name);
		if (workflowStarted) {
			this.toastr.success(`Job ${name} is gestart.`);
			setTimeout(() => {
				this.refreshWorkflows$.next();
			}, 5 * 1000);
		} else {
			this.toastr.error(`Starten van job ${name} is mislukt.`);
		}
	}

	async deactivate(cronWorkflow: CronWorkflow) {
		await this.etlService.deactivateCronWorkflow(cronWorkflow);
		this.refreshCronWorkflows$.next();
	}

	async activate(cronWorkflow: CronWorkflow) {
		await this.etlService.activateCronWorkflow(cronWorkflow);
		this.refreshCronWorkflows$.next();
	}

	private mapCronWorkflow(cronWorkflow: CronWorkflow, workflows: Workflow[]) {
		const metadata = cronWorkflow.metadata;
		const spec = cronWorkflow.spec;

		let laatsteRun = 'Nooit';
		const lastScheduledTime = cronWorkflow.status.lastScheduledTime;
		if (lastScheduledTime) laatsteRun = new Date(lastScheduledTime).toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam' });

		const schedules = cronWorkflow.spec.schedules;
		let volgendeRun = 'Gedeactiveerd';

		const active = !spec.suspend;
		if (active) {
			volgendeRun = schedules
				.map((schedule) =>
					CronExpressionParser.parse(schedule, { tz: 'Europe/Amsterdam' })
						.next()
						.toDate()
						.toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam' })
				)
				.join(', ');
		}

		const cronTooltip = <TooltipElement[]>[
			{
				label: 'Schedule',
				value: schedules.map((schedule) => cronstrue.toString(schedule, { use24HourTimeFormat: true, locale: 'nl' })),
			},
			{
				label: 'Laatste run',
				value: laatsteRun,
			},
			{
				label: 'Volgende run',
				value: volgendeRun,
			},
		];

		const lastWorkflow = workflows
			.filter((workflow) => workflow.metadata.name.startsWith(metadata.name))
			.sort((a, b) => new Date(b.status.startedAt!).getTime() - new Date(a.status.startedAt!).getTime())[0];

		return {
			uid: metadata.uid,
			name: metadata.name.replace('-cron', '').replaceAll('-', ' '),
			schedules,
			active,
			cronTooltip,
			description: nameToDescription.get(metadata.name) || 'Geen beschrijving beschikbaar',
			cronWorkflow,
			lastWorkflow,
		};
	}

	private mapWorkflow(workflow: Workflow) {
		const metadata = workflow.metadata;
		const status = workflow.status;
		const finishedAt = status.finishedAt;
		const startedAt = status.startedAt;
		return {
			uid: metadata.uid,
			name: metadata.name,
			startTime: new Date(startedAt!).toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam' }),
			finishTime: finishedAt ? new Date(finishedAt).toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam' }) : undefined,
			workflow,
		};
	}

	openDetails(workflow: Workflow) {
		this.router.navigate(['etl', workflow.metadata.uid]);
	}
}
